import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useMyProfile } from '@/features/profile';
import { Async } from '@/shared/ui/Async';
import { Button } from '@/shared/ui/Button';

import { RequestCard } from '../components/RequestCard';
import type { MatchedRequest } from '../model/matched-request';
import { useMatchedRequests } from '../hooks/use-coverage';

type FeedFilter = 'best' | 'nearest' | 'jumuah';

/** Client-side sort/filter of the matched feed. `best` keeps the RPC's score
 *  ranking; `nearest` re-sorts by distance; `jumuah` narrows to requests that
 *  include the Jumu'ah slot (still score-ranked). */
function applyFilter(requests: readonly MatchedRequest[], filter: FeedFilter): MatchedRequest[] {
  if (filter === 'nearest') {
    return [...requests].sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity));
  }
  if (filter === 'jumuah') {
    return requests.filter((r) => r.prayerSlots.includes('jumuah'));
  }
  return [...requests];
}

/** Providers browse open requests, ranked by proximity + availability (Phase 2). */
export function BrowseRequestsRoute(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useMyProfile();
  const matched = useMatchedRequests();
  const [filter, setFilter] = useState<FeedFilter>('best');

  const filters: { key: FeedFilter; label: string }[] = [
    { key: 'best', label: t('match.filterBest') },
    { key: 'nearest', label: t('match.filterNearest') },
    { key: 'jumuah', label: t('match.filterJumuahOnly') },
  ];

  // A provider with no service area gets nothing from the match RPC — prompt them
  // to set a location rather than showing a confusing empty feed.
  if (profile.data && profile.data.serviceLat === null) {
    return (
      <main className="page stack">
        <h1>{t('nav.browse')}</h1>
        <div className="card stack">
          <p>{t('match.setLocationPrompt')}</p>
          <Button onClick={() => navigate('/profile/edit')}>{t('match.setLocationCta')}</Button>
        </div>
      </main>
    );
  }

  const skeletons = (
    <div className="stack" aria-hidden="true">
      {[0, 1, 2].map((n) => (
        <div key={n} className="skeleton-card" />
      ))}
    </div>
  );

  return (
    <main className="page stack">
      <h1>{t('nav.browse')}</h1>
      <div className="segmented" role="tablist" aria-label={t('nav.browse')}>
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={`segmented__item${filter === f.key ? ' segmented__item--active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <Async
        query={matched}
        loading={skeletons}
        isEmpty={(l) => l.length === 0}
        emptyMessage={t('match.emptyNearby')}
      >
        {(requests) => <FeedList requests={requests} filter={filter} />}
      </Async>
    </main>
  );
}

/** Renders the filtered/sorted feed. The first card (in `best` order) is the top
 *  match — flagged with the "best for you" pill when its score is in the high band. */
function FeedList({
  requests,
  filter,
}: {
  requests: readonly MatchedRequest[];
  filter: FeedFilter;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shown = useMemo(() => applyFilter(requests, filter), [requests, filter]);

  if (shown.length === 0) {
    return (
      <div className="state">
        <p className="muted">{t('match.emptyNearby')}</p>
      </div>
    );
  }

  return (
    <div className="stack">
      {shown.map((request, i) => (
        <RequestCard
          key={request.id}
          request={request}
          distanceKm={request.distanceKm}
          matchScore={request.score}
          topMatch={filter === 'best' && i === 0 && request.score >= 0.66}
          onOpen={() => navigate(`/requests/${request.id}`)}
        />
      ))}
    </div>
  );
}
